// useCountUp — animates a number from 0 (or a starting value) to the
// target on mount. Uses requestAnimationFrame for smooth 60fps and an
// easeOutCubic curve so the number whips up fast then settles.
//
// Used by every KPI card on the dashboard so the hero numbers feel
// alive instead of just appearing.

import { useEffect, useRef, useState } from 'react';

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export function useCountUp(target: number, durationMs = 900) {
  const [value, setValue] = useState(0);
  const startedAt = useRef(0);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    const targetN = Number.isFinite(target) ? target : 0;
    const from = 0;
    startedAt.current = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startedAt.current;
      const progress = Math.min(1, elapsed / durationMs);
      const eased = easeOutCubic(progress);
      const current = from + (targetN - from) * eased;
      setValue(current);
      if (progress < 1) {
        rafId.current = requestAnimationFrame(tick);
      } else {
        setValue(targetN);
        rafId.current = null;
      }
    };
    rafId.current = requestAnimationFrame(tick);
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [target, durationMs]);

  return value;
}
